
// Libraries
import React, { useEffect, useState, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { Button, Modal } from 'antd'
import { Formik, Form } from 'formik'
import PropTypes from 'prop-types'
// API
import { epagAPI } from '../../../../API/epagAPI'

// Hooks
import { useInputValue } from '../../../../hooks/useInputValue'
import { useGetToken } from '../../../../hooks/useGetToken'

// Utils
import { displayUTCDate, validateSpelling } from '../../../../utils/utils'

// Styles
import { LastNameWrapper, NameWrapper } from '../styles'

// Components
import { LetterCounter } from '../../LetterCounter'
import TextInput from '../../../Base/Forms/TextInput'
import TextArea from '../../../Base/Forms/TextArea'
import ImageInput from '../../../Base/Forms/ImageInput'
import { ButtonContainer } from '../../../Base/Forms/styles'

// Constants
import { Text } from '../../../../constants/TextConstants'
import { SUMMARY_MODAL_SCHEMA } from './schema'
import { SUMMARY_MODAL_SIZES } from '../../../../constants/modals'

export const ModalSummary = ({ initialState, visible, isSavedChange, title, setSummaryModal, setModelProfile }) => {
  const linkProfileImage = useInputValue()
  const uploadedImage = useInputValue()
  const role = useInputValue(initialState.role)
  const grade = useInputValue(initialState.grade)
  const coreSkill = useInputValue(initialState.core_skill)
  const since = useInputValue(initialState.since)
  const description = useInputValue(initialState.summary)
  const name = useInputValue(initialState.name)
  const lastName = useInputValue(initialState.lastName)
  const { token } = useGetToken()
  const [initialDescription, setInitialDescription] = useState('')
  const [descriptionSpellingError, setDescriptionSpellingError] = useState('')
  const timer = useRef(null)
  const delayVerifySpelling = 1000
  const { userId } = useParams()
  const isOwnProfile = !userId

  useEffect(() => {
    if (visible) {
      const isUpdateParametersFromEwa = isOwnProfile ? updateParametersFromEwa() : false
      if (!isUpdateParametersFromEwa) {
        role.setValue(initialState.role)
        grade.setValue(initialState.grade)
        coreSkill.setValue(initialState.core_skill)
        since.setValue(initialState.since)
      }

      description.setValue(initialState.summary)
      name.setValue(initialState.name)
      lastName.setValue(initialState.lastName)
      linkProfileImage.setValue(initialState.profile_image)
      uploadedImage.setValue(initialState.uploaded_image)
      role.setError(false)
      grade.setError(false)
      coreSkill.setError(false)
      description.setError(false)
      name.setError(false)
      lastName.setError(false)
      linkProfileImage.setError(false)
      uploadedImage.setError(false)
      setInitialDescription(initialState.summary)
    }
  }, [visible])

  const onSubmit = async (values) => {
    setModelProfile((prevState) => ({
      ...prevState,
      summary: values.description,
      name: values.name,
      lastName: values.lastName,
      uploaded_image: values.uploadedImage,
      profile_image: values.linkProfileImage
    }))
    setSummaryModal(false)
  }

  const verifySpelling = (value, setError) => {
    validateSpelling(token, value, null, setError)
  }

  const verifySpellingWitDelay = (value, setError) => {
    clearTimeout(timer.current)
    timer.current = setTimeout(() => verifySpelling(value, setError), delayVerifySpelling)
  }

  const onCancel = () => {
    setSummaryModal(false)
    if (description.value !== initialDescription) {
      isSavedChange(true)
    }
  }
  const updateParametersFromEwa = () => {
    if (token) {
      epagAPI
        .UpdateProfile(token)
        .then((response) => {
          if (response) {
            role.setValue(response.role)
            grade.setValue(response.grade)
            coreSkill.setValue(response.core_skill)
            since.setValue(response.since)
            // TODO: this needs improvements:
            // let's avoid mutate object directly
            initialState.role = response.role
            initialState.grade = response.grade
            initialState.core_skill = response.core_skill
            initialState.since = response.since
            initialState.discipline = response.discipline
            initialState.DU = response.DU
            return true
          }
        })
        .catch((error) => {
          console.error('updateParametersFromEwa :', error)
          setSummaryModal(false)
        })
    }
    return false
  }

  return (
    <Modal
      title={title}
      open={visible}
      okButtonProps={{ 'data-testid': 'save-button' }}
      maskClosable={false}
      okText={Text.Add}
      width='80%'
      destroyOnClose={true}
      onCancel={onCancel}
      keyboard={false}
      footer={null}>
      <Formik
        initialValues={{
          name: name.value,
          lastName: lastName.value,
          since: since.value ? displayUTCDate(since.value) : '',
          role: role.value,
          grade: grade.value,
          coreSkill: coreSkill.value,
          linkProfileImage: linkProfileImage.value,
          uploadedImage: uploadedImage.value,
          description: description.value
        }}
        validationSchema={SUMMARY_MODAL_SCHEMA()}
        onSubmit={(values) => {
          onSubmit(values)
        }}
      >
        {({ values, isValid, setValues, handleSubmit, setFieldError, setFieldTouched }) => {
          useEffect(() => {
            if (descriptionSpellingError) {
              setFieldError('description', Text.SpellErrors + descriptionSpellingError)
              setFieldTouched('description', true, false)
            }
          }, [descriptionSpellingError, setFieldError, setFieldTouched])

          return (
            <Form>
              <NameWrapper>
                <TextInput
                  name='name'
                  title={Text.Name}
                  disabled={true}
                  data-testid='NameProfile1'
                />
                <LastNameWrapper>
                  <TextInput
                    name='lastName'
                    title={Text.LastName}
                    disabled={true}
                    data-testid='LastNameProfile'
                  />
                </LastNameWrapper>
                <LastNameWrapper>
                  <TextInput
                    name='since'
                    title={Text.SinceDate}
                    disabled={true}
                    data-testid='SinceDateProfile'
                  />
                </LastNameWrapper>
              </NameWrapper>

              <NameWrapper>
                <TextInput
                  name='role'
                  title={Text.Role}
                  placeholder={Text.Developer}
                  disabled={true}
                  data-testid='DescriptionTitleProfile'
                />
                <LastNameWrapper>
                  <TextInput
                    name='grade'
                    title={Text.Grade}
                    placeholder={Text.Developer}
                    disabled={true}
                    data-testid='DescriptionTitleProfile'
                  />
                </LastNameWrapper>
                <LastNameWrapper>
                  <TextInput
                    name='coreSkill'
                    title={Text.CoreSkill}
                    placeholder={Text.JavaScript}
                    disabled={true}
                    data-testid='DescriptionProfile'
                  />
                </LastNameWrapper>
              </NameWrapper>
              <NameWrapper>
                <ImageInput
                  name='uploadedImage'
                  title={Text.ProfileImage}
                  data-testid={'ProfileImageInput'}
                />
              </NameWrapper>
              <TextArea
                name='description'
                title={Text.Summary}
                data-testid='summary-input'
                maxLength={(SUMMARY_MODAL_SIZES.maxLengthSummary + 1)}
                onKeyUp={() => verifySpellingWitDelay(values.description, setDescriptionSpellingError)}
                onChange={(e) => {
                  setValues({
                    ...values,
                    description: e?.target?.value
                  })
                  isSavedChange(false)
                }}
              />
              <div>
                <LetterCounter actual={values?.description ? values.description.length : 0} max={SUMMARY_MODAL_SIZES.maxLengthSummary} />
              </div>
              <ButtonContainer>
                <Button onClick={onCancel} style={{ marginRight: '10px' }}>
                  Cancel
                </Button>
                <Button type='primary' onClick={() => handleSubmit()} disabled={!isValid} data-testid='save-button'>
                  Add
                </Button>
              </ButtonContainer>
            </Form>
          )
        }}
      </Formik>
    </Modal>
  )
}

ModalSummary.propTypes = {
  setModelProfile: PropTypes.func,
  setSummaryModal: PropTypes.func,
  visible: PropTypes.bool,
  title: PropTypes.string,
  initialState: PropTypes.object,
  isSavedChange: PropTypes.func
}
